import {useEffect, useRef, useState} from 'react';
import {
  fetchAndCacheLandmarkImage,
  optimizeLandmarkImageUrl,
  shouldIgnoreStoredLandmarkUrl,
  type LandmarkImageResult,
} from '@/utils/landmarkImages';

type UseLandmarkImageOptions = {
  skip?: boolean;
  onImageResolved?: (result: LandmarkImageResult) => void;
  forceRefresh?: boolean;
  identityKey?: string | null;
  countryName?: string | null;
};

export const useLandmarkImage = (
  destination?: string | null,
  initialUrl?: string | null,
  options?: UseLandmarkImageOptions,
) => {
  const {skip = false, onImageResolved, forceRefresh = false, identityKey, countryName} = options || {};

  const sanitizedInitialUrl =
    initialUrl && !shouldIgnoreStoredLandmarkUrl(initialUrl) ? initialUrl : null;
  const normalizedInitialUrl = optimizeLandmarkImageUrl(sanitizedInitialUrl);

  const [imageUrl, setImageUrl] = useState<string | null>(normalizedInitialUrl ?? null);
  const [isLoading, setIsLoading] = useState<boolean>(() => {
    if (!destination || skip) return false;
    return !normalizedInitialUrl;
  });
  const onResolvedRef = useRef<UseLandmarkImageOptions['onImageResolved']>(onImageResolved);

  useEffect(() => {
    onResolvedRef.current = onImageResolved;
  }, [onImageResolved]);

  useEffect(() => {
    if (!destination || skip) {
      setImageUrl(normalizedInitialUrl ?? null);
      setIsLoading(false);
      return;
    }

    if (normalizedInitialUrl) {
      setImageUrl(normalizedInitialUrl);
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    fetchAndCacheLandmarkImage(destination, {forceRefresh, identityKey, countryName})
      .then((result) => {
        if (cancelled || !result?.url) {
          return;
        }
        setImageUrl(optimizeLandmarkImageUrl(result.url) ?? result.url);
        onResolvedRef.current?.(result);
      })
      .catch((error) => {
        console.warn('[useLandmarkImage] failed to resolve image', error);
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [destination, skip, normalizedInitialUrl, forceRefresh, identityKey, countryName]);

  return {
    imageUrl,
    isLoading,
  };
};
